/**
 * belief.ts — the agent's Bayesian belief map (its SPATIAL MEMORY).
 *
 * Perception is Bayesian, NOT learned — the learning budget goes to the
 * policy (the linear Q-function in qnet.ts). This is the deliberate design
 * recorded in the plan: a hybrid agent with a classical perceiver and a
 * learned mover.
 *
 * Each cell carries two beliefs in [0, 1]: bGold and bStone. They are updated
 * recursively from a noisy inverse-sensor model. The agent DOES NOT know a
 * priori whether a distant sensed cell is gold or a stone — it must learn
 * this from its sensor's history.
 *
 * ---
 *
 * **The bilateral update — do not simplify this.** A reading of GOLD must pull
 * bStone → 0, a reading of STONE must pull bGold → 0, a reading of FREE must
 * pull both → 0. The three cell classes are mutually exclusive (a cell is one
 * of FREE / GOLD / STONE, never two), so evidence for one is evidence
 * against the others. An earlier version updated only the matching channel
 * and left stale belief on the other channel; that crippled the baseline and
 * made the learned agent look artificially good. Keep the bilateral update.
 *
 * ---
 *
 * **The recency channel (bRecent).** Alongside bGold/bStone we keep a decaying
 * trace of when each cell was last visited or sensed. 1.0 = the agent is/was
 * just here this tick; it decays multiplicatively every tick by
 * `recencyDecay` (default 0.97 → memory horizon ~33 ticks). This is the
 * agent's SPATIAL MEMORY: candidate features (features.ts) read it as
 * `recentDensityOnPath` so the Q-function can notice "this direction leads
 * through recently-visited cells — try a different way". No hand-coded
 * frontier detection — the recency trace IS the learnable frontier signal.
 *
 * ---
 *
 * **The `known` cache.** The candidate features (features.ts) need a
 * three-way cell classification: UNKNOWN / FREE / OBSTACLE. We maintain it
 * alongside the probabilistic belief so the feature computation stays cheap.
 * A cell becomes FREE the moment the agent visits it or forms a strong
 * belief either way; it becomes OBSTACLE when bStone ≥ 0.5. Unsensed cells
 * stay UNKNOWN until resolved.
 */

/** True cell classes — the World's ground truth (also used as sensor readings). */
export const FREE = 0;
export const GOLD = 1;
export const STONE = 2;

/** Three-way classification (the `known` cache). */
export const UNKNOWN = 0;
export const FREE_CELL = 1;
export const OBSTACLE = 2;

/** Default sensor reliability — P(reading == truth). */
export const SENSOR_P_CORRECT = 0.85;
/** Default Bayesian learning rate for one sensor reading. */
export const BELIEF_LR = 0.5;
/** Default obstacle classification threshold on bStone. */
export const OBSTACLE_THRESH = 0.5;
/** Default per-tick decay of the recency channel. 0.97 → memory horizon ~33
 *  ticks; tunable from the UI.
 *
 *  This is the LEARNED-FEATURE memory mechanism: the linear Q-function reads
 *  `recentDensityOnPath` (features.ts) and learns that low-recency directions
 *  are candidate frontiers. */
export const DEFAULT_RECENCY_DECAY = 0.97;
/** Sensor radius (Manhattan). 3 → the agent senses a 7×7 neighbourhood. */
export const SENSOR_RADIUS = 3;

export class Belief {
  readonly grid: number;
  /** P(cell is gold) per cell, flat row-major [r*grid + c]. */
  readonly bGold: Float64Array;
  /** P(cell is stone) per cell, flat row-major. */
  readonly bStone: Float64Array;
  /** Recency-of-contact per cell, in [0,1]. 1.0 = the agent is/was just at
   *  this cell this tick; decays by `recencyDecay` every tick. */
  readonly bRecent: Float64Array;
  /** Three-way classification cache (UNKNOWN/FREE_CELL/OBSTACLE). */
  readonly known: Uint8Array;
  /** True once the agent has visited the cell (a stronger "free" signal than
   *  sensing — the agent is standing there). */
  readonly visited: Uint8Array;
  /** Per-tick multiplicative decay applied to `bRecent`. */
  recencyDecay: number;

  constructor(grid: number) {
    this.grid = grid;
    this.bGold = new Float64Array(grid * grid);
    this.bStone = new Float64Array(grid * grid);
    this.bRecent = new Float64Array(grid * grid);
    this.known = new Uint8Array(grid * grid);
    this.visited = new Uint8Array(grid * grid);
    this.recencyDecay = DEFAULT_RECENCY_DECAY;
  }

  /** Reset to the agent's priors before any sensing. `priorGold` is the
   *  fraction of the grid that is goldy (a reasonable cold prior); `priorBar`
   *  is a small constant. The agent's own starting cell is forced FREE.
   *  Recency is zeroed (nothing has been visited yet). */
  reset(priorGold: number, priorBar: number, startR: number, startC: number): void {
    this.bGold.fill(priorGold);
    this.bStone.fill(priorBar);
    this.bRecent.fill(0);
    this.known.fill(UNKNOWN);
    this.visited.fill(0);
    this.clearCell(startR, startC); // agent knows where it starts
  }

  /** Decay every cell's recency by `recencyDecay`. Called ONCE per env step,
   *  BEFORE the agent re-senses its new neighbourhood. The cells the agent
   *  then visits/senses get `bRecent ← 1` (a fresh contact), everything else
   *  keeps decaying. */
  decayRecency(): void {
    const d = this.recencyDecay;
    const r = this.bRecent;
    for (let i = 0; i < r.length; i++) r[i] *= d;
  }

  /** Mark a cell as definitely FREE (the agent is standing on it). Zeros both
   *  beliefs, marks it visited, refreshes recency (the agent is HERE). */
  clearCell(r: number, c: number): void {
    const i = r * this.grid + c;
    this.bGold[i] = 0;
    this.bStone[i] = 0;
    this.bRecent[i] = 1;
    this.known[i] = FREE_CELL;
    this.visited[i] = 1;
  }

  /**
   * Apply one noisy sensor reading at (r, c). The bilateral update enforces
   * mutual exclusion: evidence for GOLD pulls bStone down, evidence for STONE
   * pulls bGold down, evidence for FREE pulls both down. After the update we
   * refresh the `known` cache.
   *
   * Sensing also bumps `bRecent[i] ← 1` so the recency channel can tell
   * sensed cells from never-touched ones.
   */
  applyReading(r: number, c: number, reading: number, lr = BELIEF_LR): void {
    const i = r * this.grid + c;
    // Visited cells are certain FREE — never let a noisy reading un-clear them.
    if (this.visited[i]) {
      this.bGold[i] = 0;
      this.bStone[i] = 0;
      this.bRecent[i] = 1;
      this.known[i] = FREE_CELL;
      return;
    }
    // Sensing a cell is a "recent contact" — bump its recency.
    this.bRecent[i] = 1;
    const decay = 1 - lr;
    if (reading === GOLD) {
      this.bGold[i] = decay * this.bGold[i] + lr;
      this.bStone[i] = decay * this.bStone[i];
    } else if (reading === STONE) {
      this.bStone[i] = decay * this.bStone[i] + lr;
      this.bGold[i] = decay * this.bGold[i];
    } else {
      // FREE
      this.bGold[i] = decay * this.bGold[i];
      this.bStone[i] = decay * this.bStone[i];
    }
    // Refresh the three-way classification.
    if (this.visited[i]) {
      this.known[i] = FREE_CELL;
    } else if (this.bStone[i] >= OBSTACLE_THRESH) {
      this.known[i] = OBSTACLE;
    } else if (this.bGold[i] >= OBSTACLE_THRESH) {
      // Strong believed-gold counts as "resolved" too — it's not unknown
      // anymore. Classify as FREE_CELL so it isn't treated as an obstacle.
      this.known[i] = FREE_CELL;
    } else {
      this.known[i] = UNKNOWN;
    }
  }
}

/**
 * Sample one noisy sensor reading for a cell whose true class is `truth`.
 * With probability pCorrect the reading equals truth; otherwise it is a
 * uniformly-random wrong class. Matches the prototype's inverse-sensor model.
 */
export function sampleReading(
  truth: number,
  pCorrect: number,
  rng: () => number,
): number {
  if (rng() < pCorrect) return truth;
  // Pick uniformly from the other two classes.
  const wrong = [FREE, GOLD, STONE].filter((x) => x !== truth);
  return wrong[Math.floor(rng() * wrong.length)];
}
